import { Link } from 'react-router-dom';

const guideLinks = [
  { label: 'Shoes Guide', to: '/guide/shoes', icon: 'ri-footprint-line', desc: 'Discover the best footwear collections and sneaker releases' },
  { label: 'Clothing Guide', to: '/guide/clothing', icon: 'ri-t-shirt-line', desc: 'Browse hoodies, t-shirts, jackets and more' },
  { label: 'Safety Guide', to: '/guide/safety', icon: 'ri-shield-check-line', desc: 'Learn about our safety practices and privacy commitment' },
  { label: 'Beginner Guide', to: '/guide/beginner', icon: 'ri-compass-line', desc: 'Start here for a step-by-step introduction' },
  { label: 'Benefits', to: '/guide/benefits', icon: 'ri-star-line', desc: 'See why organized shopping saves time and money' },
  { label: 'Alternatives', to: '/guide/alternatives', icon: 'ri-git-branch-line', desc: 'Compare Superbuy Sheet with other methods' },
  { label: 'How to Use', to: '/guide/how-to-use', icon: 'ri-guide-line', desc: 'Master every feature with detailed tutorials' },
  { label: 'Shipping Guide', to: '/guide/shipping', icon: 'ri-ship-line', desc: 'Everything about international delivery and tracking' },
  { label: 'FAQ', to: '/guide/faq', icon: 'ri-question-answer-line', desc: 'Get quick answers to the most common questions' },
];

export default function SeoContent() {
  return (
    <section className="w-full px-6 md:px-10 py-14 md:py-20 bg-background-50">
      <div className="max-w-4xl mx-auto">
        <article>
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-8 text-center">
            What is Superbuy Sheet? Your Complete Shopping Companion
          </h2>

          <div className="space-y-6 text-foreground-700 leading-relaxed">
            <p className="text-base md:text-lg">
              A <strong className="text-foreground-950">Superbuy Sheet</strong> is an organized digital
              product catalog designed to transform how shoppers discover, compare, and access quality
              products online. Unlike traditional marketplaces that scatter product information across
              countless pages, a well-structured Superbuy Sheet brings everything together in one
              accessible, easy-to-navigate platform — making it the preferred choice for savvy shoppers
              who value both efficiency and quality in their purchasing journey. Learn more in our
              <Link to="/guide/beginner" className="text-primary-500 hover:text-primary-600 ml-1 cursor-pointer">beginner guide</Link>.
            </p>

            <p>
              Whether you are hunting for the latest sneaker releases, browsing seasonal hoodie
              collections, or searching for unique accessories that express your personal style, the
              Superbuy Sheet approach simplifies everything. Each product category is methodically
              organized with direct links, detailed specifications, and visual references, eliminating
              the frustration of jumping between dozens of browser tabs just to compare a few items.
              Our <Link to="/guide/shoes" className="text-primary-500 hover:text-primary-600 cursor-pointer">shoes guide</Link> and
              <Link to="/guide/clothing" className="text-primary-500 hover:text-primary-600 mx-1 cursor-pointer">clothing guide</Link>
              provide deep dives into the most popular categories.
            </p>

            <p>
              The platform covers fourteen distinct categories including <strong className="text-foreground-950">Shoes</strong>,
              <strong className="text-foreground-950"> Hoodies &amp; Sweaters</strong>, <strong className="text-foreground-950">T-Shirts</strong>,
              <strong className="text-foreground-950"> Jackets</strong>, <strong className="text-foreground-950">Pants &amp; Shorts</strong>,
              <strong className="text-foreground-950"> Headwear</strong>, <strong className="text-foreground-950">Sets</strong>, and more.
              Each category features regularly updated product collections that reflect current trends,
              seasonal must-haves, and timeless classics. For shoppers concerned about security, our
              <Link to="/guide/safety" className="text-primary-500 hover:text-primary-600 mx-1 cursor-pointer">safety guide</Link>
              covers everything from privacy to secure browsing. Check our
              <Link to="/guide/benefits" className="text-primary-500 hover:text-primary-600 mx-1 cursor-pointer">benefits overview</Link>
              for a complete look at how Superbuy Sheet improves your shopping experience.
            </p>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/guide"
              className="inline-flex items-center gap-2 px-7 py-3 bg-accent-500 text-background-50 font-semibold text-base rounded-full hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Read the Complete Guide
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
            </Link>
          </div>
        </article>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-background-100/70 rounded-xl border border-background-200/50">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-search-line text-lg"></i>
            </span>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Easy Discovery</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Find exactly what you are looking for across 14 curated categories with organized product links and detailed previews.
            </p>
          </div>

          <div className="p-6 bg-background-100/70 rounded-xl border border-background-200/50">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-time-line text-lg"></i>
            </span>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Save Time</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              No more endless scrolling. Access thousands of products instantly with direct links to each category on our main platform.
            </p>
          </div>

          <div className="p-6 bg-background-100/70 rounded-xl border border-background-200/50">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
              <i className="ri-shield-check-line text-lg"></i>
            </span>
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">Trusted Resource</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              We provide transparent, organized shopping resources with clear product categorization for confident purchasing decisions.
            </p>
          </div>
        </div>

        {/* SEO Guide Links Grid */}
        <div className="mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-6 text-center">
            Explore Our Shopping Guides
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {guideLinks.map((guide) => (
              <Link
                key={guide.to}
                to={guide.to}
                className="group flex items-start gap-3 p-4 bg-background-100/70 rounded-xl border border-background-200/50 hover:border-primary-300 hover:bg-background-50 transition-all duration-200 cursor-pointer"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                  <i className={`${guide.icon} text-base`}></i>
                </span>
                <div>
                  <h4 className="font-semibold text-foreground-950 text-sm group-hover:text-primary-500 transition-colors">
                    {guide.label}
                  </h4>
                  <p className="text-xs text-foreground-500 leading-relaxed mt-0.5">
                    {guide.desc}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Additional Keyword Coverage */}
        <div className="mt-14 p-8 bg-background-100/70 rounded-2xl border border-background-200/50">
          <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4 text-center">
            Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-foreground-950 text-sm mb-1">
                Is Superbuy Sheet safe for shopping?
              </h4>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Yes. Superbuy Sheet is a discovery platform that does not process payments or collect personal data. All purchases happen securely on kaspreadsheet.com. Read our <Link to="/guide/safety" className="text-primary-500 hover:text-primary-600 cursor-pointer">full safety guide</Link>.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-950 text-sm mb-1">
                How does Superbuy Sheet shipping work?
              </h4>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Shipping is handled through kaspreadsheet.com with international delivery options. See our <Link to="/guide/shipping" className="text-primary-500 hover:text-primary-600 cursor-pointer">shipping guide</Link> for detailed information.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-950 text-sm mb-1">
                What are Superbuy Sheet alternatives?
              </h4>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Compare Superbuy Sheet with marketplace browsing, manual spreadsheets, and social media discovery in our <Link to="/guide/alternatives" className="text-primary-500 hover:text-primary-600 cursor-pointer">alternatives guide</Link>.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-950 text-sm mb-1">
                How do I use Superbuy Sheet for beginners?
              </h4>
              <p className="text-xs text-foreground-600 leading-relaxed">
                Start with our <Link to="/guide/beginner" className="text-primary-500 hover:text-primary-600 cursor-pointer">beginner guide</Link> and <Link to="/guide/how-to-use" className="text-primary-500 hover:text-primary-600 cursor-pointer">how-to-use tutorial</Link> for step-by-step instructions on maximizing the platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}